import React, { useState } from "react";
import { SectionId, API_SIDEBAR } from "./SidebarConfig";
import { SendSingleSection } from "./Sendsinglesection";
import { SendBulkSection } from "./Sendbulksection";
import { PlaceholderSection } from "./Placeholdersection";
import { ApiSidebar } from "./Apisidebar";
import { GetTemplatesSection } from "./GetTemplateSection";
import { OverviewSection } from "./Overview";
import { AddTemplateUtilitySection } from "./AddTemplate/UtilityTemplate";
import { AddTemplateAuthSection } from "./AddTemplate/AuthenticationTemplate";
import { AddTemplateMarketingSection } from "./AddTemplate/MarketingTemplates";
import { MessageReportSection } from "./MessageReportSection";
import { AddTrainBotRuleSection } from "./TrainBot/AddTrainBotRuleSection";
import { GetTrainBotSection } from "./TrainBot/GetTrainBotSection";
import { TokenFile } from "./TokenFile";

const SECTION_MAP: Record<SectionId, React.ReactNode> = {
  "send-single": <SendSingleSection />,
  "send-bulk": <SendBulkSection />,
  "get-template": <GetTemplatesSection />,
  "add-template-auth": <AddTemplateAuthSection />,
  "add-template-marketing": <AddTemplateMarketingSection />,
  "add-template-utility": <AddTemplateUtilitySection />,
  "message-report": <MessageReportSection />,
  "add-rules": <AddTrainBotRuleSection />,
  "get-rules": <GetTrainBotSection />,
  overview: <OverviewSection />,
  token: <TokenFile />,
  errors: <PlaceholderSection title="Errors & Codes" />,
};

export function ApiDocumentationWebsite() {
  const [activeSection, setActiveSection] = useState<SectionId>("add-template-auth");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSectionChange = (id: SectionId) => {
    setActiveSection(id);
    setSidebarOpen(false);
  };

  return (
    <div className="bg-web-background text-white min-h-screen">

      {/* ── Mobile sticky header ── */}
      <div className="md:hidden sticky top-0 z-40 bg-web-background border-b border-white/10 px-4 h-14 flex items-center justify-between">
        <span className="text-sm font-semibold">API Docs</span>
        <button
          onClick={() => setSidebarOpen(true)}
          aria-label="Open navigation"
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          {/* Hamburger icon */}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect y="3" width="20" height="2" rx="1" fill="currentColor" />
            <rect y="9" width="20" height="2" rx="1" fill="currentColor" />
            <rect y="15" width="20" height="2" rx="1" fill="currentColor" />
          </svg>
        </button>
      </div>

      {/* ── Backdrop ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Mobile drawer ── */}
      <div
        className={`
          fixed top-0 left-0 h-full z-50 bg-web-background
          w-[80vw] max-w-[300px]
          flex flex-col
          transform transition-transform duration-300 ease-in-out md:hidden
          border-r border-white/10
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-white/10 shrink-0">
          <span className="text-sm font-semibold">API Docs</span>
          <button
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation"
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable sidebar content */}
        <div className="flex-1 overflow-y-auto py-4">
          <ApiSidebar active={activeSection} onChange={handleSectionChange} />
        </div>
      </div>

      {/* ── Page body ── */}
      <div className="lg:px-24 md:px-12 px-4 md:py-20 py-6">
        <div className="flex items-start gap-8 min-h-[calc(100vh-56px)]">

          {/* Desktop sidebar */}
          <div className="hidden md:block shrink-0">
            <ApiSidebar active={activeSection} onChange={setActiveSection} />
          </div>

          {/* Main content */}
          <main className="flex-1 min-w-0 md:px-8 px-0">
            <div className="max-w-3xl mx-auto">
              {SECTION_MAP[activeSection]}
            </div>
          </main>

        </div>
      </div>
    </div>
  );
}